import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/config";
import { userService } from "../services/userService";
import { ActiveSubscription } from "../types/subscription";

type SubscriptionContextValue = {
  subscription: ActiveSubscription | null;
  loading: boolean;

  // derived helpers
  hasSubscription: boolean;
  isPlus: boolean;
  isPlusOrHigher: boolean;
  isPremium: boolean;
  isPlatinum: boolean;
  isDefault: boolean;

  // 🔁 manual refresh trigger
  refreshSubscription: () => Promise<void>;
};

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [subscription, setSubscription] = useState<ActiveSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  const loadSubscription = async (uid: string) => {
    const sub = await userService.getUserSubscription(uid);
    setSubscription(sub);
  };

  const refreshSubscription = async () => {
    if (!userId) return;
    setLoading(true);
    await loadSubscription(userId);
    setLoading(false);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setSubscription(null);
        setUserId(null);
        setLoading(false);
        return;
      }

      setUserId(user.uid);
      setLoading(true);
      await loadSubscription(user.uid);
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const value: SubscriptionContextValue = {
    subscription,
    loading,
    refreshSubscription,

    hasSubscription:
      !!subscription &&
      subscription.subscriptionType !== "default" &&
      subscription.subscriptionVia !== "default",

    isPlus: subscription?.subscriptionType === "plus",
    isPlusOrHigher:
      subscription?.subscriptionType === "plus" ||
      subscription?.subscriptionType === "premium" ||
      subscription?.subscriptionType === "platinum" ||
      subscription?.subscriptionType === "coach",
    isPremium: subscription?.subscriptionType === "premium" || subscription?.subscriptionType === "platinum" || subscription?.subscriptionType === "coach",
    isPlatinum: subscription?.subscriptionType === "platinum",
    isDefault: !subscription || subscription.subscriptionType === "default",
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return ctx;
}
